import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Search, User, Users, CheckCircle2, UserCheck } from 'lucide-react';

export const TransferPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Use FocusedLayout (no sidebar) when coming from Super App
  const fromSuperApp = location.state?.fromSuperApp !== false;
  const Layout = fromSuperApp ? FocusedLayout : UserLayout;
  const [wallets, setWallets] = useState([]);
  const [recipient, setRecipient] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [currency, setCurrency] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [directory, setDirectory] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [source, setSource] = useState('directory'); // 'directory' | 'contacts'
  const [directoryLoading, setDirectoryLoading] = useState(false);

  useEffect(() => {
    const preset = searchParams.get('recipient');
    if (preset) setRecipient(preset);
    userAPI.getWallets().then((res) => setWallets(res.data));
    userAPI.getContacts().then((res) => setContacts(res.data)).catch(() => {});
    loadDirectory('');
  }, [searchParams]);

  const loadDirectory = async (q) => {
    setDirectoryLoading(true);
    try {
      const { data } = await userAPI.getDirectory(q, 0, 30);
      setDirectory(data);
    } catch (error) {
      // silent - don't spam
    } finally { setDirectoryLoading(false); }
  };

  useEffect(() => {
    const handle = setTimeout(() => loadDirectory(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const selectedWallet = wallets.find(w => w.currency === currency);

  const pickUser = (u) => {
    setSelectedUser(u);
    setRecipient(u.email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recipient || !currency || !amount) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await userAPI.transfer(recipient, currency, parseFloat(amount));
      toast.success(`Transfer of ${amount} ${currency} sent successfully!`);
      setRecipient('');
      setSelectedUser(null);
      setAmount('');
      userAPI.getWallets().then(res => setWallets(res.data));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Transfer failed');
    } finally { setLoading(false); }
  };

  return (
    <Layout title="Transfer Money">
      <div className="space-y-6" data-testid="transfer-page">
        <div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Transfer Funds</h1>
          <p className="mt-2 text-muted-foreground">Send funds instantly to other platform users</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader><CardTitle>Send to User</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Recipient (Email or Username)</Label>
                    <Input
                      value={recipient}
                      onChange={(e) => { setRecipient(e.target.value); setSelectedUser(null); }}
                      placeholder="user@example.com or username"
                      data-testid="transfer-recipient-input"
                      className="h-11"
                      required
                    />
                    {selectedUser && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200">
                        <User className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">Selected: <strong>{selectedUser.name || selectedUser.username || selectedUser.email}</strong></span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger data-testid="transfer-currency-select" className="h-11">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {wallets.map(w => (
                          <SelectItem key={w.currency} value={w.currency}>
                            {w.currency} (Balance: {w.balance.toFixed(2)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      type="number" step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      data-testid="transfer-amount-input"
                      className="h-11"
                      required
                    />
                    {selectedWallet && <p className="text-xs text-muted-foreground">Available: {selectedWallet.balance.toFixed(2)} {currency}</p>}
                  </div>

                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      Transfers between users are instant and free of charge.
                    </p>
                  </div>

                  <Button type="submit" disabled={loading} className="w-full h-11" data-testid="transfer-submit-button">
                    {loading ? 'Sending...' : 'Send Transfer'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card data-testid="user-directory">
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <CardTitle className="text-lg">Pick recipient</CardTitle>
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant={source === 'contacts' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSource('contacts')}
                    data-testid="transfer-source-contacts"
                  >
                    <UserCheck className="h-3 w-3 mr-1" /> Contacts ({contacts.length})
                  </Button>
                  <Button
                    type="button"
                    variant={source === 'directory' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSource('directory')}
                    data-testid="transfer-source-directory"
                  >
                    <Users className="h-3 w-3 mr-1" /> All users
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Click a user to auto-fill the recipient field</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {source === 'directory' && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-9 h-10"
                      placeholder="Search users..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="user-directory-search"
                    />
                  </div>
                )}
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {source === 'contacts' ? (
                    contacts.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-4">No saved contacts yet. Add contacts from a user&apos;s profile.</p>
                    ) : contacts.map((c) => {
                      const uid = c.contact_user_id;
                      const label = c.nickname || c.contact_name || c.contact_username || c.contact_email;
                      const u = { id: uid, email: c.contact_email, username: c.contact_username, name: c.contact_name || c.nickname };
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => pickUser(u)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                            selectedUser?.id === uid ? 'bg-blue-100 dark:bg-blue-950 border border-blue-400' : 'hover:bg-muted border border-transparent'
                          }`}
                          data-testid={`contact-pick-${uid}`}
                        >
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm shrink-0">
                            {(label || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{label}</p>
                            <p className="text-xs text-muted-foreground truncate">{c.contact_email}</p>
                          </div>
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        </button>
                      );
                    })
                  ) : (
                    directoryLoading && directory.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-4">Loading...</p>
                    ) : directory.length === 0 ? (
                      <p className="text-center text-sm text-muted-foreground py-4">No users found</p>
                    ) : (
                      directory.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => pickUser(u)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                            selectedUser?.id === u.id ? 'bg-blue-100 dark:bg-blue-950 border border-blue-400' : 'hover:bg-muted border border-transparent'
                          }`}
                          data-testid={`directory-user-${u.id}`}
                        >
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm shrink-0">
                            {(u.username || u.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">@{u.username || u.email?.split('@')[0] || 'user'}</p>
                            <p className="text-xs text-muted-foreground truncate">{u.name || 'User'}</p>
                          </div>
                        </button>
                      ))
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};
